import React, { useEffect, useState } from "react";
import { FormGroup, Button, Input, Label } from "reactstrap";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import CKEditor from "react-ckeditor-component";

const AddPlaceDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const {
    AuthReducer: { auth },
  } = useSelector((state) => state);

  const [scheduledTimes, setScheduledTimes] = useState({});
  const [selectedDate, setSelectedDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [isClosed, setIsClosed] = useState(false);

  const [specialClosures, setSpecialClosures] = useState([]);

  const [fields, setFields] = useState([]);
  const [dynamicFields, setDynamicFields] = useState([
    { id: "", heading: "", description: "" },
  ]);

  const options = [
    { value: "monday", label: "Monday" },
    { value: "tuesday", label: "Tuesday" },
    { value: "wednesday", label: "Wednesday" },
    { value: "thursday", label: "Thursday" },
    { value: "friday", label: "Friday" },
    { value: "saturday", label: "Saturday" },
    { value: "sunday", label: "Sunday" },
  ];

  // ✅ Load schedule & closures
  useEffect(() => {
    axios(`/api/admin/v1/update-schedule/${id}`, {
      headers: { Authorization: `Bearer ${auth?.token}` },
    })
      .then((res) => {
        const scheduleData = res.data?.data;

        if (scheduleData?.schedule_timing) {
          setScheduledTimes(JSON.parse(scheduleData.schedule_timing));
        }

        if (scheduleData?.special_closures) {
          setSpecialClosures(JSON.parse(scheduleData.special_closures));
        }
      })
      .catch((err) => console.error("Schedule fetch error:", err));
  }, [auth, id]);

  const handleDateChange = (event) => setSelectedDate(event.target.value);

  // Convert 24-hour to 12-hour format with AM/PM
  const formatTo12Hour = (time24) => {
    if (!time24) return '';
    const [hours, minutes] = time24.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  // Format time slot with 12-hour format
  const formatTimeSlot = (timeSlot) => {
    if (timeSlot === 'Closed') return 'Closed';
    const [startTime, endTime] = timeSlot.split(' - ');
    return `${formatTo12Hour(startTime)} - ${formatTo12Hour(endTime)}`;
  };

  // ✅ Weekly schedule handler with duplicate/overlap check
  const handleTimeChange = () => {
    if (selectedDate && (isClosed || (startTime && endTime))) {
      const timeSlot = isClosed ? "Closed" : `${startTime} - ${endTime}`;

      setScheduledTimes((prev) => {
        const existingSlots = prev[selectedDate] || [];

        // 1. Already Closed
        if (isClosed && existingSlots.includes("Closed")) {
          toast.error(`${selectedDate} is already marked Closed.`);
          return prev;
        }

        // 2. Duplicate
        if (existingSlots.includes(timeSlot)) {
          toast.error("This time slot already exists!");
          return prev;
        }

        // 3. Overlap check
        if (!isClosed) {
          const newStart = new Date(`1970-01-01T${startTime}:00`);
          const newEnd = new Date(`1970-01-01T${endTime}:00`);

          const isOverlap = existingSlots.some((slot) => {
            if (slot === "Closed") return false;
            const [s, e] = slot.split(" - ");
            const slotStart = new Date(`1970-01-01T${s}:00`);
            const slotEnd = new Date(`1970-01-01T${e}:00`);

            return (
              (newStart >= slotStart && newStart < slotEnd) || // overlaps start
              (newEnd > slotStart && newEnd <= slotEnd) || // overlaps end
              (newStart <= slotStart && newEnd >= slotEnd) // covers whole slot
            );
          });

          if (isOverlap) {
            toast.error("This time slot overlaps with an existing one!");
            return prev;
          }
        }

        // ✅ Add slot
        const updatedSlots = [...existingSlots, timeSlot];

        // Auto sort by start time
        const sortedSlots = updatedSlots.filter((slot) => slot !== "Closed")
          .sort((a, b) => {
            const [aStart] = a.split(" - ");
            const [bStart] = b.split(" - ");
            return (
              new Date(`1970-01-01T${aStart}:00`) -
              new Date(`1970-01-01T${bStart}:00`)
            );
          });

        if (updatedSlots.includes("Closed")) sortedSlots.unshift("Closed");

        return {
          ...prev,
          [selectedDate]: sortedSlots,
        };
      });

      setStartTime("");
      setEndTime("");
      setIsClosed(false);
    } else {
      toast.error("Please select a day and set start/end time or mark as closed.");
    }
  };

  const handleDeleteTimeSlot = (event, day, timeToDelete) => {
    event.preventDefault();
    setScheduledTimes((prev) => {
      const updated = { ...prev };
      updated[day] = updated[day].filter((time) => time !== timeToDelete);
      if (updated[day].length === 0) delete updated[day];
      return updated;
    });
  };

  // 👉 Special closures
  const handleAddSpecialClosure = (date) => {
    if (!date) return toast.error("Please pick a date.");
    if (specialClosures.includes(date)) {
      toast.error("This date is already marked as closed.");
      return;
    }
    setSpecialClosures([...specialClosures, date]);
  };

  const handleDeleteSpecialClosure = (date) => {
    setSpecialClosures(specialClosures.filter((d) => d !== date));
  };

  const handleSubmitSchedule = () => {
    if (Object.keys(scheduledTimes).length === 0) {
      toast.error("Please schedule at least one time slot.");
      return;
    }
    const form = new FormData();
    form.append("schedule_timing", JSON.stringify(scheduledTimes));

    axios.put(
  `/api/admin/v1/update-schedule/${id}`,
  { schedule_timing: JSON.stringify(scheduledTimes) }, // ✅ send string
  {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${auth?.token}`,
    },
  }
).then((res) => toast.success(res.data?.message))
      .catch(() => toast.error("Error saving schedule."));
  };

  const handleSubmitClosures = () => {
    const form = new FormData();
    form.append("specialClosures", JSON.stringify(specialClosures));

    axios.put(
  `/api/admin/v1/update-closures/${id}`,
  { specialClosures:  JSON.stringify(specialClosures) },
  { headers: { Authorization: `Bearer ${auth?.token}` } }
).then(() => toast.success("Special closures updated!"))
      .catch(() => toast.error("Error saving closures."));
  };
  const handleAddField = () => {
    setDynamicFields([
      ...dynamicFields,
      { id: "", heading: "", description: "" },
    ]);
  };

  const handleFieldChange = (type, index, key, value) => {
    const targetFields = type === "dynamic" ? dynamicFields : fields;
    const updatedFields = [...targetFields];
    if (index < 0 || index >= updatedFields.length) return;

    updatedFields[index][key] = value;
    type === "dynamic"
      ? setDynamicFields(updatedFields)
      : setFields(updatedFields);
  };

  const handleDeleteField = (type, index) => {
    const targetFields = type === "dynamic" ? dynamicFields : fields;
    const updatedFields = targetFields.filter((_, i) => i !== index);
    type === "dynamic"
      ? setDynamicFields(updatedFields)
      : setFields(updatedFields);
  };

  const handleSubmitFields = () => {
    if (dynamicFields.some((field) => !field.heading || !field.description)) {
      toast.error("All dynamic fields must have a heading and description.");
      return;
    }
    const form = new FormData();
    form.append("dynamicFields", JSON.stringify([...fields, ...dynamicFields]));

    axios
      .put(`/api/admin/v1/add-update-dynamic-fields/${id}`, form, {
        headers: { Authorization: `Bearer ${auth?.token}` },
      })
      .then((res) => {
        toast.success(res.data?.message);
        navigate(-1);
      })
      .catch(() => toast.error("An error occurred."));
  };

  return (
    <div>
      <div className="row my-3">
        <div className="col-md-10">
          <h4 className="text-white">Add Place Details</h4>
        </div>
      </div>

      <div className="bg-white px-3 py-4 rounded-2 my-4">
        <div className="row">
          {/* Weekly Schedule */}
          <div className="col-md-8">
            <h4>Weekly Schedule</h4>
            <FormGroup>
              <Label>Select a Day</Label>
              <Input
                type="select"
                className="form-control text-dark"
                value={selectedDate}
                onChange={handleDateChange}
              >
                <option value="">Select a day</option>
                {options.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Input>
            </FormGroup>

            {selectedDate && (
              <div className="row gy-3 mt-3">
                <div className="col-md-4">
                  <FormGroup>
                    <Label>Start Time</Label>
                    <Input
                      type="time"
                      value={startTime}
                      onChange={(e) => setStartTime(e.target.value)}
                      disabled={isClosed}
                    />
                  </FormGroup>
                </div>
                <div className="col-md-4">
                  <FormGroup>
                    <Label>End Time</Label>
                    <Input
                      type="time"
                      value={endTime}
                      onChange={(e) => setEndTime(e.target.value)}
                      disabled={isClosed}
                    />
                  </FormGroup>
                </div>
                <div className="col-md-2 mt-4">
                  <Button color="primary" onClick={handleTimeChange}>
                    Set
                  </Button>
                </div>
                <div className="col-md-2 mt-4">
                  <Label>Closed</Label>
                  <Input
                    type="checkbox"
                    checked={isClosed}
                    onChange={(e) => setIsClosed(e.target.checked)}
                  />
                </div>
              </div>
            )}

            <div className="text-dark mt-3">
              <strong>Scheduled Times: </strong>
              <ul>
                {Object.entries(scheduledTimes).map(([day, times]) =>
                  (Array.isArray(times) ? times : []).map((time, index) => (
                    <li key={`${day}-${index}`}>
                      <strong>
                        {day.charAt(0).toUpperCase() + day.slice(1)} {formatTimeSlot(time)}
                      </strong>
                      <button
                        className="btn btn-warning btn-sm ms-2 mt-1"
                        onClick={(event) =>
                          handleDeleteTimeSlot(event, day, time)
                        }
                      >
                        Delete
                      </button>
                    </li>
                  ))
                )}
              </ul>
            </div>
            <Button
              color="success"
              className="mt-3"
              onClick={handleSubmitSchedule}
            >
              Save Weekly Schedule
            </Button>
          </div>

          {/* Special Closures */}
          <div className="col-md-8 mt-5">
            <h4>Special Closures (Festivals / Vacations)</h4>
            <FormGroup className="d-flex gap-2">
              <Input type="date" id="closureDate" />
              <Button
                color="danger"
                onClick={() =>
                  handleAddSpecialClosure(
                    document.getElementById("closureDate").value
                  )
                }
              >
                Add Closure
              </Button>
            </FormGroup>

            <ul className="text-dark">
              {specialClosures.map((date, index) => (
                <li key={index}>
                  {date}
                  <Button
                    color="warning"
                    size="sm"
                    className="ms-2"
                    onClick={() => handleDeleteSpecialClosure(date)}
                  >
                    Delete
                  </Button>
                </li>
              ))}
            </ul>
            <Button
              color="primary"
              className="mt-3"
              onClick={handleSubmitClosures}
            >
              Save Special Closures
            </Button>
          </div>

          {/* Dynamic Fields */}
          <div className="col-md-10 mt-5">
            <h2>Place Detail Fields</h2>
            {fields.map((field, index) => (
              <div key={`field-${index}`} className="form-group mt-3 text-dark">
                <Label className="h5">Heading:</Label>
                <Input
                  type="text"
                  value={field.heading}
                  onChange={(e) =>
                    handleFieldChange("fields", index, "heading", e.target.value)
                  }
                  placeholder="Enter Heading"
                />
                <Label className="h5">Content:</Label>
                <FormGroup>
                  <CKEditor
                    activeClass="p10"
                    content={field.description}
                    events={{
                      change: (evt) => {
                        const data = evt.editor.getData();
                        handleFieldChange("fields", index, "description", data);
                      },
                    }}
                  />
                </FormGroup>
                <Button
                  color="danger"
                  className="mt-2"
                  onClick={() => handleDeleteField("fields", index)}
                >
                  Delete
                </Button>
              </div>
            ))}
            {dynamicFields.map((field, index) => (
              <div
                key={`dynamic-${index}`}
                className="form-group mt-3 text-dark"
              >
                <Label className="h5">Heading:</Label>
                <Input
                  type="text"
                  value={field.heading}
                  onChange={(e) =>
                    handleFieldChange(
                      "dynamic",
                      index,
                      "heading",
                      e.target.value
                    )
                  }
                  placeholder="Enter Heading"
                />
                <Label className="h5">Content:</Label>
                <FormGroup>
                  <CKEditor
                    content={field.description}
                    events={{
                      change: (evt) => {
                        const data = evt.editor.getData();
                        handleFieldChange("dynamic", index, "description", data);
                      },
                    }}
                  />
                </FormGroup>
                <Button
                  color="danger"
                  className="mt-2"
                  onClick={() => handleDeleteField("dynamic", index)}
                >
                  Delete
                </Button>
              </div>
            ))}
            <Button color="primary" className="mt-2" onClick={handleAddField}>
              Add Field
            </Button>
          </div>

          <div className="col-md-4 mt-4">
            <Button color="success" onClick={handleSubmitFields}>
              Submit Fields
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddPlaceDetails;
